import { useState, useEffect } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import axiosInstance from '../../lib/axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Label,
  Tooltip,
} from 'recharts';
import { toast, ToastContainer } from 'react-toastify';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';

const RevnueFlow = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const globalProperty = useSelector(selectGlobalProperty);

  const OCCUPANCY_RANGES = [
    { label: "0-20 % Very low", min: 0, max: 20, color: "#ff6666", borderColor: "#cc3333" },
    { label: "21-40 % Low", min: 21, max: 40, color: "#FFB347", borderColor: "#cc8400" },
    { label: "41-60 % Fair", min: 41, max: 60, color: "#cccc00", borderColor: "#999900" },
    { label: "61-80 % Good", min: 61, max: 80, color: "#3399cc", borderColor: "#007acc" },
    { label: "81-100 % Very good", min: 81, max: 100, color: "#009966", borderColor: "#007a4d" },
  ];
  
  const getColorByOccupancy = (value) => {
    return OCCUPANCY_RANGES.find(range => value >= range.min && value <= range.max);
  };

  useEffect(() => {
    const fetchOccupancyData = async () => {
      try {
        const today = new Date();
        const bookDate = today.toISOString().split('T')[0];
        const checkoutDate = new Date(today);
        checkoutDate.setMonth(checkoutDate.getMonth() + 11);
        const checkoutDateStr = checkoutDate.toISOString().split('T')[0];

        const response = await axiosInstance.post('BedCalendar/GenerateBedCatCal', {
          PropId: globalProperty,
          BookDtFrom: bookDate,
          CheckoutDt: checkoutDateStr,
          Flag: 'Summary',
        });

        const { Status, Data } = response.data;

        if (Status === 0 && Data?.BedCatCalList?.length > 0) {
          const grouped = {};

          Data.BedCatCalList.forEach(item => {
            const month = item.MonthId - 1;
            const year = item.YearNm;
            const label = `${new Date(year, month).toLocaleString('default', { month: 'short' })}-${year}`;

            if (!grouped[label]) {
              grouped[label] = { name: label, booked: 0, occupied: 0, bedsToSale: 0 };
            }

            grouped[label].booked += Number(item.BookingBeds) || 0;
            grouped[label].occupied += Number(item.OccBeds) || 0;
            grouped[label].bedsToSale += Number(item.BedToSale) || 0;
          });

          const formatted = Object.values(grouped).map(entry => {
            // const total = entry.booked + entry.occupied; // Original with the booked + occupied
            const total = entry.occupied;
            const occupancyPercent = entry.bedsToSale ? (total / entry.bedsToSale) * 100 : 0;
            return {
              ...entry,
              occupancy: parseFloat(occupancyPercent.toFixed(2)),
            };
          });

          // Sort by date
          formatted.sort((a, b) => {
            const [aMonth, aYear] = a.name.split('-');
            const [bMonth, bYear] = b.name.split('-');
            return new Date(`${aMonth} 1, ${aYear}`) - new Date(`${bMonth} 1, ${bYear}`);
          });

          setData(formatted);

        } else {
          setError('No occupancy data found.');
        }
      } catch (err) {
        setError('Failed to load occupancy data.');
      } finally {
        setLoading(false);
      }
    };

    fetchOccupancyData();
  }, [globalProperty]);

  if (loading) return <Typography>Loading...</Typography>;
  if (error) return <Typography>Error: {error}</Typography>;

  return (
    <Box
      sx={{
        maxWidth: '100%',
        px: 1,
        marginLeft: '0px',
        fontFamily: "'Roboto', sans-serif",      
      }}
    >
      <ToastContainer />
      {/* Title */}
      <Typography
        variant="h5"
        sx={{ mt: 2, mb: 1, color: '#635bff', fontWeight: 600, textAlign: 'center', userSelect: 'none' }}
      >
        Monthly Bed Status
      </Typography>

      {/* Legend */}
      <Stack
        direction="row"
        spacing={4}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          fontSize: '1.5rem',
          color: '#4b5563',
        }}
      >
      </Stack>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={data} margin={{ top: 15, right: 15, bottom: 90, left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: '#4b5563' }}
            angle={-35}
            textAnchor="end"
            height={70}
            interval={0}
          >
            <Label
              value="Month's"
              offset={3}
              position="insideBottom"
              style={{ fill: '#4b5563', fontWeight: 'bold', fontSize: 16 }}
            />
          </XAxis>
          <YAxis
            domain={[0, 100]}
            tickFormatter={(value) => `${value}%`}
            tick={{ fontSize: 12, fill: '#4b5563' }}
            width={70}
          >
            <Label
              value="Occupancy %"
              angle={-90}
              position="insideLeft"
              offset={-10}
              style={{
                textAnchor: 'middle',
                fill: '#4b5563',
                fontWeight: 'bold',
                fontSize: 16,
              }}
            />
          </YAxis>
          <Tooltip
            formatter={(value) => `${value}%`}
            labelFormatter={(label) => `Month: ${label}`}
          />
          <Line
            type="monotone"
            dataKey="occupancy"
            stroke="#3b82f6"
            strokeWidth={3}
            activeDot={{ r: 6 }}
            name="Occupancy %"
            dot={(props) => {
              const { cx, cy, payload } = props;
              const range = getColorByOccupancy(payload.occupancy);
              const fillColor = range?.color || '#3b82f6';
              const borderColor = range?.borderColor || '#3b82f6';

              return (
                <>
                  <circle
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill={fillColor}
                    stroke={borderColor}
                    strokeWidth={2}
                  />
                  <text
                    x={cx}
                    y={cy - 10}
                    fontSize={12}
                    fill={borderColor}
                    textAnchor="middle"
                  >
                    {`${payload.occupancy}%`}
                  </text>
                </>
              );
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RevnueFlow;